import context from "lib/stores/context";
import apiData from "lib/stores/apiData";
import { Tab } from "@headlessui/react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { useMediaQuery } from "usehooks-ts";
import styles from "./index.module.scss";
import SidebarSummary from "./SidebarSummary";
import classNames from "classnames";
import SidebarPanels from "components/Sidebar/SidebarPanels";
import SidebarTabs from "./SidebarTabs";
import MapControlsToolbarsMobile from "../MapControlsToolbars/Mobile";
import setSidebarSelectedTab from "lib/helpers/setSidebarSelectedTab";
import getSidebarSelectedTab from "lib/helpers/getSidebarSelectedTab";

const Sidebar = () => {
  const portrait = useMediaQuery("(max-width: 1023px)");
  const { activeView, isCollapsed, activeTabWorldwide, activeTabCountries, activeTabRegions, activeCountry } = useSnapshot(context);

  // References for the layout animation.
  const sidebarRef = useRef<HTMLDivElement>();
  const scrollRef = useRef<HTMLDivElement>();
  const sidebarTabsRef = useRef<HTMLDivElement>();
  const SidebarTabsOffset = useRef<number>();
  let listener;

  useEffect(() => {
    if (!scrollRef.current) return;
    const handleTouchMove = e => {
      if (isCollapsed) {
        // e.preventDefault();
      }
    };

    try {
      scrollRef.current.removeEventListener("touchmove", handleTouchMove);
    } catch (e) {}

    // Cleanup
  }, [scrollRef.current, isCollapsed]);

  useEffect(() => {
    if (!scrollRef.current) return;
    if (isCollapsed) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isCollapsed]);

  useEffect(() => {
    sidebarRef.current.style.transition = "none";
    document.addEventListener("load", () => {
      sidebarRef.current.style.transition = "none";
      context.isCollapsed = true;
      setTimeout(() => {
        sidebarRef.current.style.transition = "transform 0.3s cubic-bezier(.05, .69, .14, 1)";
      }, 500);
    });

    const listener = () => {
      if (isCollapsed && window.innerWidth <= 1200) {
        const height =
          document.getElementById("sidebar-header").getBoundingClientRect().height +
          document.getElementById("products-bar-mobile").getBoundingClientRect().height;
        const viewport = window.visualViewport.height - window.innerHeight;
        if (window.innerWidth <= 1200) {
          document.getElementById("sidebar-tabs").classList.add("tabs-within-collapsed");
          if (sidebarRef.current.style.transform === `translateY(${window.innerHeight - height - viewport}px)`) {
            return;
          }
          sidebarRef.current.style.transform = `translateY(${window.innerHeight - height - viewport}px)`;
        } else {
          if (sidebarRef.current.style.transform === `translateY(${window.innerHeight - height}px)`) {
            return;
          }
          sidebarRef.current.style.transform = `translateY(${window.innerHeight - height}px)`;
        }
      } else {
        document.getElementById("sidebar-tabs").classList.remove("tabs-within-collapsed");
        if (
          sidebarRef.current.style.transform === "translateY(160px)" ||
          sidebarRef.current.style.transform === "translateY(80px)" ||
          sidebarRef.current.style.transform === "translateY(140px)"
        ) {
          return;
        }
        if (window.innerWidth <= 1200 && window.innerWidth > 1023) {
          sidebarRef.current.style.transition = "transform 0.3s cubic-bezier(.05, .69, .14, 1)";
          sidebarRef.current.style.transform = "translateY(160px)";
        }
        if (window.innerWidth < 1023 && window.innerWidth > 768) {
          sidebarRef.current.style.transition = "transform 0.3s cubic-bezier(.05, .69, .14, 1)";
          sidebarRef.current.style.transform = "translateY(140px)";
        }
        if (window.innerWidth <= 768) {
          sidebarRef.current.style.transition = "transform 0.3s cubic-bezier(.05, .69, .14, 1)";
          sidebarRef.current.style.transform = "translateY(80px)";
        }
      }
    }
    window.addEventListener("resize", listener);
    return () => {
      window.removeEventListener("resize", listener);
    };
  }, []);

  useEffect(() => {
    if (isCollapsed && window.innerWidth <= 1023) {
      const height =
        document.getElementById("sidebar-header").getBoundingClientRect().height +
        document.getElementById("products-bar-mobile").getBoundingClientRect().height;
      const viewport = window.visualViewport.height - window.innerHeight;
      if (window.innerWidth <= 768) {
        if (sidebarRef.current.style.transform === `translateY(${window.innerHeight - height - viewport}px)`) {
          return;
        }
        sidebarRef.current.style.transform = `translateY(${window.innerHeight - height - viewport}px)`;
      } else {
        if (sidebarRef.current.style.transform === `translateY(${window.innerHeight - height}px)`) {
          return;
        }
        sidebarRef.current.style.transform = `translateY(${window.innerHeight - height}px)`;
      }
    } else {
      if (
        sidebarRef.current.style.transform === "translateY(160px)" ||
        sidebarRef.current.style.transform === "translateY(80px)" ||
        sidebarRef.current.style.transform === "translateY(140px)"
      ) {
        return;
      }
      if (window.innerWidth > 1023) {
        sidebarRef.current.style.transition = "transform 0.3s cubic-bezier(.05, .69, .14, 1)";
        sidebarRef.current.style.transform = "translateY(176px)";
      }
      if (window.innerWidth <= 1023 && window.innerWidth > 768) {
        sidebarRef.current.style.transition = "transform 0.3s cubic-bezier(.05, .69, .14, 1)";
        sidebarRef.current.style.transform = "translateY(156px)";
      }
      if (window.innerWidth <= 768) {
        sidebarRef.current.style.transition = "transform 0.3s cubic-bezier(.05, .69, .14, 1)";
        sidebarRef.current.style.transform = "translateY(96px)";
      }
    }
  }, [isCollapsed, activeView, activeTabWorldwide, activeTabCountries, activeTabRegions, activeCountry, portrait]);

  return (
    <div
      id="sidebar-container"
      className={classNames([
        styles.container,
        portrait && !isCollapsed ? "!overflow-x-hidden !pointer-events-auto" : "overflow-x-visible"
      ])}
      ref={sidebarRef}
    >
      <Tab.Group
        selectedIndex={getSidebarSelectedTab({
          activeView,
          activeTabWorldwide,
          activeTabCountries,
          activeTabRegions
        })}
        onChange={index => {
          if (window.innerWidth <= 1023) {
            context.isCollapsed = false;
          }
          setSidebarSelectedTab(index);
        }}
      >
        <div id="products-bar-mobile" className={classNames(styles["products-bar-mobile"])}>
          <MapControlsToolbarsMobile />
        </div>
        <div ref={scrollRef} className={classNames(styles.root, isCollapsed ? "sidebar-collapsed" : "")}>
          <div id="sidebar-header" className={"pointer-events-auto w-full top-0 z-50"}>
            <SidebarSummary />
            <SidebarTabs />
          </div>
          <SidebarPanels ref={sidebarTabsRef} offset={SidebarTabsOffset} />
        </div>
      </Tab.Group>
    </div>
  );
};

export default Sidebar;
